import { useTranslation } from 'react-i18next'
import { CustomContentProps, SnackbarKey, useSnackbar } from 'notistack'
import { createContext, useCallback, useEffect, useState, ReactNode, useContext, useRef, useMemo } from 'react'

import { Pocket } from '../utils/dexie/db'
import { UserContext } from './user.context'
import { AuthContext } from './auth.context'
import { countriesCurrencies } from '../lib'
import { useLocation } from 'react-router-dom'
import { useSocket } from '../hooks/use-socket'
import { getHomeData } from '../api/app-data.api'
import { resolveAppAccessToken, canLoadDashboardAppData, resolvePaySystemUserId } from '../sso/services/app-access.service'
import { AppStateContextType } from '../types/context.types'
import { BankingPartnersBalance } from '../types/payment.types'
import { Bank, Customer, CustomerCompany, UserSessionType } from '../types/user.types'
import { handleDeleteDataByOne, handleGetDataFromTableById } from '../utils/db/common-methods'
import { getBankingPartnersBalanceByCountryData, getCurrentPocketsDataByCountry, getCustomersBriefData, getProcessingBatchesData } from '../api/app-queries.api'
import { getJsonSize, upsertCustomerWithLimitAtStart } from '../utils'

export const AppStateContext = createContext<Partial<AppStateContextType>>({})

const bankInitialState = [
  {
    name: '',
    achCode: '',
    swiftCodes: [],
    createdAt: null
  }
]

interface ReportCompleteProps extends CustomContentProps {
  reportType?: string
  downloadLink?: string
  allowDownload?: boolean
}

const Provider = ({ children }: { children: ReactNode }) => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

  const { t } = useTranslation(['common'])
  const location = useLocation()
  const { isAuth } = useContext(AuthContext)
  const { userSession } = useContext(UserContext)
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dashboardDataLoadRef = useRef<Promise<void> | null>(null)

  const [homeStats, setHomeStats] = useState<any>(null)
  const [scrollToTop, setScrollToTop] = useState<boolean>(false)
  const [processingBatch, setProcessingBatch] = useState<boolean>(false)
  const [currentPocketsData, setCurrentPocketsData] = useState<Array<Pocket>>([])
  const [customersBriefData, setCustomersBriefData] = useState<AppStateContextType['customersBriefData']>(null)
  const [defaultPocketIdSelected, setDefaultPocketIdSelected] = useState<string>('')
  const [showProcessingSnackbar, setShowProcessingSnackbar] = useState<boolean>(false)
  const [customerCompanyData, setCustomerCompanyData] = useState<CustomerCompany | null>(null)
  const [bankingPartnersBalanceByCountry, setBankingPartnersBalanceByCountry] = useState<Array<BankingPartnersBalance>>([])

  const [isLoading, setIsLoading] = useState<boolean>(() => {
    if (globalThis.window !== undefined) {
      const storedValue = globalThis.sessionStorage.getItem('isLoading')
      return storedValue ? storedValue === 'true' : false
    }
    return false
  })
  const [USDDollarRate, setUSDDollarRate] = useState<number>(() => {
    if (globalThis.window !== undefined) {
      const storedValue = globalThis.sessionStorage.getItem('USDDollarRate')
      return storedValue ? Number(storedValue) : 0
    }
    return 0
  })
  const [banksData, setBanksData] = useState<Array<Bank>>(() => {
    if (globalThis.window !== undefined) {
      const storedBanksData = globalThis.sessionStorage.getItem('banksData')
      return storedBanksData ? JSON.parse(storedBanksData) : [...bankInitialState]
    }
    return [...bankInitialState]
  })
  const [banksBalancesDataLoaded, setBanksBalancesDataLoaded] = useState<boolean>(() => {
    if (globalThis.window !== undefined) {
      const storedValue = globalThis.sessionStorage.getItem('banksBalancesDataLoaded')
      return storedValue ? storedValue === 'true' : false
    }
    return false
  })

  useEffect(() => {
    if (!appDataSocket) return

    if (!userSession) return

    if (!defaultPocketIdSelected) return

    appDataSocket.on('addCustomerCompanyPocket', async (dataFromSocket: any) => {
      const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
      const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

      if (selectedCurrency === dataFromSocket.currency) {
        const accessToken = await resolveAppAccessToken()
        handleGetPocketsDataFromRemote?.(userSession.belongsTo, userSession, selectedCountry, accessToken)
      }
    })

    appDataSocket.on('updateCustomerCompanyPocket', async (dataFromSocket: any) => {
      const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
      const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

      if (selectedCurrency === dataFromSocket.currency) {
        const accessToken = await resolveAppAccessToken()
        handleGetPocketsDataFromRemote?.(userSession.belongsTo, userSession, selectedCountry, accessToken)
      }
    })

    appDataSocket.on(`addNewCustomer:${selectedCountry}`, async (dataFromSocket: any) => {
      const localCustomersBriefData = globalThis.sessionStorage.getItem(`customersBriefData:${userSession?._id}:${selectedCountry}`) || JSON.stringify({ customersData: [] })
      const customersDataParsed = JSON.parse(localCustomersBriefData)

      handleSetCustomersBriefData(userSession, customersDataParsed, dataFromSocket)
    })

    appDataSocket.on(`updateCustomer:${selectedCountry}`, async (dataFromSocket: any) => {
      const localCustomersBriefData = globalThis.sessionStorage.getItem(`customersBriefData:${userSession?._id}:${selectedCountry}`) || JSON.stringify({ customersData: [] })
      const customersDataParsed = JSON.parse(localCustomersBriefData)

      if (dataFromSocket.deleted === false) {
        handleSetCustomersBriefData(userSession, customersDataParsed, dataFromSocket)
      }

      if (dataFromSocket.deleted === true) {
        const customersDataUpdated = customersDataParsed.customersData.filter((customerData: Customer) => String(customerData._id) !== String(dataFromSocket._id))

        if (userSession?.belongsTo !== 'holdings' && userSession?.role === 'super-admin-client') {
          setCustomersBriefData({ customersData: customersDataUpdated } as any)
        }

        if (userSession?.belongsTo !== 'holdings' && (userSession?.role === 'admin-client' || userSession?.role === 'admin-client-builder')) {
          const customerCompanyPocketsRelated = dataFromSocket.customerCompanyPocketsRelated

          if (customerCompanyPocketsRelated.includes(defaultPocketIdSelected)) {
            setCustomersBriefData({ customersData: customersDataUpdated } as any)
          }
        }
      }
    })

    appDataSocket.on(`deleteCustomer:${selectedCountry}`, async (dataFromSocket: any) => {
      const localCustomersBriefData = globalThis.sessionStorage.getItem(`customersBriefData:${userSession?._id}:${selectedCountry}`) || JSON.stringify({ customersData: [] })
      const customersDataParsed = JSON.parse(localCustomersBriefData)

      const customersDataUpdated = customersDataParsed.customersData.filter((customerData: Customer) => String(customerData._id) !== String(dataFromSocket._id))

      if (userSession?.belongsTo !== 'holdings' && userSession?.role === 'super-admin-client') {
        setCustomersBriefData({ customersData: customersDataUpdated } as any)
      }

      if (userSession?.belongsTo !== 'holdings' && (userSession?.role === 'admin-client' || userSession?.role === 'admin-client-builder')) {
        const customerCompanyPocketsRelated = dataFromSocket.customerCompanyPocketsRelated

        if (customerCompanyPocketsRelated.includes(defaultPocketIdSelected)) {
          setCustomersBriefData({ customersData: customersDataUpdated } as any)
        }
      }
    })

    appDataSocket.on('addNewBatch', async (dataFromSocket: any) => {
      const currency = dataFromSocket.currency || 'cop'
      const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

      if (currency === selectedCurrency) {
        const accessToken = await resolveAppAccessToken()
        handleGetBatchesBriefDataFromRemote(String(userSession._id), accessToken)
      }
    })

    appDataSocket.on('updateBatch', async (dataFromSocket: any) => {
      const currency = dataFromSocket.currency || 'cop'
      const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

      if (currency === selectedCurrency) {
        const accessToken = await resolveAppAccessToken()
        handleGetBatchesBriefDataFromRemote(String(userSession._id), accessToken)
      }
    })

    appDataSocket.on('deleteBatch', async (dataFromSocket: any) => {
      const currency = dataFromSocket.currency || 'cop'
      const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

      if (currency === selectedCurrency) {
        const accessToken = await resolveAppAccessToken()
        handleGetBatchesBriefDataFromRemote(String(userSession._id), accessToken)
        handleDeleteBatchFromLocalDB(dataFromSocket.documentKey)
      }
    })

    appDataSocket.on('paymentsReportCreated', ({ reportType, userSessionId, downloadLink }: { notificationKey: SnackbarKey; reportType: string; userSessionId: string; downloadLink: string }) => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
      }

      if (userSession && String(userSession._id) === String(userSessionId)) {
        closeSnackbar()

        if (location.pathname !== '/' && !location.pathname.includes('requested-reports')) {
          console.log('entro')
          enqueueSnackbar(t('notifications.reportComplete'), { persist: true, variant: 'reportComplete', anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, reportType, downloadLink } as ReportCompleteProps)
        }
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, location, appDataSocket, defaultPocketIdSelected, isAppDataSocketInitialized])

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!isAuth) {
      closeSnackbar()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth])

  const handleStopTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
    }
  }, [])

  const handleStartTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null
      handleShowCreatingReportDelayNotification?.()
    }, 20_000)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSetCustomersBriefData = (userSessionToUse: UserSessionType, customersDataParsed: any, dataFromSocket: any) => {
    if (userSessionToUse?.belongsTo !== 'holdings' && userSessionToUse?.role === 'super-admin-client') {
      const customersDataUpdated = upsertCustomerWithLimitAtStart(customersDataParsed.customersData, dataFromSocket, 10000)
      setCustomersBriefData({ customersData: customersDataUpdated } as any)
    }

    if (userSessionToUse?.belongsTo !== 'holdings' && (userSessionToUse?.role === 'admin-client' || userSessionToUse?.role === 'admin-client-builder')) {
      const customerCompanyPocketsRelated = dataFromSocket.customerCompanyPocketsRelated

      if (customerCompanyPocketsRelated.includes(defaultPocketIdSelected)) {
        const customersDataUpdated = upsertCustomerWithLimitAtStart(customersDataParsed.customersData, dataFromSocket, 10000)
        setCustomersBriefData({ customersData: customersDataUpdated } as any)
      }
    }
  }

  const handleGetClientUserAppData = async (customerCompanyId: string, userSessionToUse: UserSessionType, systemUserId: string) => {
    try {
      const accessToken = await resolveAppAccessToken()
      await Promise.all([
        handleGetBatchesBriefData(systemUserId, accessToken),
        handleGetCustomersBriefData(systemUserId, accessToken),
        handleGetCurrentPocketsData(customerCompanyId, userSessionToUse, accessToken)
      ])
    } catch (error) {
      console.log('Error handleGetClientUserAppData > ', error)
    }
  }

  const handleGetAdminUserAppData = async (systemUserId: string) => {
    try {
      const accessToken = await resolveAppAccessToken()
      await Promise.all([
        handleGetHomeDataByCountry(systemUserId, accessToken),
        handleGetBankingPartnersBalanceByCountry(systemUserId, accessToken),
        handleGetCustomersBriefData(systemUserId, accessToken)
      ])
    } catch (error) {
      console.log('Error handleGetAdminUserAppData > ', error)
    }
  }

  const loadDashboardAppData = useCallback(async (sessionOverride?: UserSessionType | null) => {
    const session = sessionOverride ?? userSession
    const systemUserId = resolvePaySystemUserId(String(session?._id))

    if (!systemUserId || !canLoadDashboardAppData(String(session?._id), isAuth)) {
      return
    }

    if (dashboardDataLoadRef.current) {
      return dashboardDataLoadRef.current
    }

    const loadPromise = (async () => {
      setIsLoading(true)
      if (globalThis.window !== undefined) {
        globalThis.sessionStorage.setItem('isLoading', 'true')
      }

      try {
        if (session?.belongsTo !== 'holdings') {
          await handleGetClientUserAppData(String(session?.belongsTo), session as UserSessionType, systemUserId)
        }

        if (session?.belongsTo === 'holdings') {
          await handleGetAdminUserAppData(systemUserId)
        }
      } finally {
        setIsLoading(false)
        if (globalThis.window !== undefined) {
          globalThis.sessionStorage.setItem('isLoading', 'false')
        }
      }
    })()

    dashboardDataLoadRef.current = loadPromise

    try {
      await loadPromise
    } finally {
      dashboardDataLoadRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, userSession])

  useEffect(() => {
    loadDashboardAppData()
  }, [loadDashboardAppData])

  useEffect(() => {
    const onSuiteDashboardReady = () => {
      loadDashboardAppData()
    }

    globalThis.addEventListener('suite-dashboard-ready', onSuiteDashboardReady)
    return () => globalThis.removeEventListener('suite-dashboard-ready', onSuiteDashboardReady)
  }, [loadDashboardAppData])

  const handleGetHomeDataByCountry = (systemUserId: string, accessToken: string) => {
    let localHomeDataStatsToSet: any = {}
    const localHomeDataStats = globalThis.sessionStorage.getItem(`homeDataStats:${selectedCountry}`)

    if (localHomeDataStats) {
      localHomeDataStatsToSet = JSON.parse(localHomeDataStats)
      setHomeStats(localHomeDataStatsToSet)
    }

    return handleGetHomeStatsByCountryFromRemote(systemUserId, accessToken)
  }

  const handleGetHomeStatsByCountryFromRemote = async (systemUserId: string, accessToken: string) => {
    try {
      const { data } = await getHomeData(systemUserId, selectedCountry, accessToken)

      globalThis.sessionStorage.setItem(`homeDataStats:${selectedCountry}`, JSON.stringify(data))
      setHomeStats(data)
    } catch (error) {
      console.log('Error handleGetHomeStatsByCountryFromRemote > ', error)
    }
  }

  const handleGetBankingPartnersBalanceByCountry = (systemUserId: string, accessToken: string) => {
    let localBankingPartnersBalanceByCountryToSet: any = {}
    const localBankingPartnersBalanceByCountry = globalThis.sessionStorage.getItem(`bankingPartnersBalance:${systemUserId}:${selectedCountry}`)

    if (localBankingPartnersBalanceByCountry) {
      localBankingPartnersBalanceByCountryToSet = JSON.parse(localBankingPartnersBalanceByCountry)
      setBankingPartnersBalanceByCountry(localBankingPartnersBalanceByCountryToSet.bankingPartnersBalances)
      setBanksBalancesDataLoaded(true)
    }

    return handleGetBankingPartnersBalanceByCountryFromRemote(systemUserId, accessToken)
  }

  const handleGetBankingPartnersBalanceByCountryFromRemote = async (systemUserId: string, accessToken: string) => {
    try {
      const { data } = await getBankingPartnersBalanceByCountryData(String(systemUserId), selectedCountry, accessToken)

      globalThis.sessionStorage.setItem(`bankingPartnersBalance:${systemUserId}:${selectedCountry}`, JSON.stringify(data))
      setBankingPartnersBalanceByCountry(data.bankingPartnersBalances)
      setBanksBalancesDataLoaded(true)
    } catch (error: any) {
      console.log('Error > handleGetBankingPartnersBalanceByCountryFromRemote > ', error, error?.response?.data?.message)
    }
  }

  const handleGetCustomersBriefData = (systemUserId: string, accessToken: string) => {
    let localCustomersByCountryToSet: any = {}
    const localCustomersByCountry = globalThis.sessionStorage.getItem(`customersBriefData:${systemUserId}:${selectedCountry}`)

    if (localCustomersByCountry) {
      const JSONSize = getJsonSize(localCustomersByCountry)
      console.log('JSONSize Customers brief data', JSONSize)
      localCustomersByCountryToSet = JSON.parse(localCustomersByCountry)
      setCustomersBriefData(localCustomersByCountryToSet)
    }

    return handleGetCustomersByCountryFromRemote(systemUserId, accessToken)
  }

  const handleGetCustomersByCountryFromRemote = async (systemUserId: string, accessToken: string) => {
    try {
      const { data } = await getCustomersBriefData(String(systemUserId), selectedCountry, accessToken)

      globalThis.sessionStorage.setItem(`customersBriefData:${systemUserId}:${selectedCountry}`, JSON.stringify(data))
      setCustomersBriefData(data)
    } catch (error: any) {
      console.log('Error > handleGetCustomersByCountryFromRemote > ', error, error?.response?.data?.message)
    }
  }

  const handleGetBatchesBriefData = (systemUserId: string, accessToken: string) => {
    let localDocumentsBriefDataToSet: any = {}
    const localDocumentsBriefData = globalThis.sessionStorage.getItem(`processingBatchesCount:${systemUserId}:${selectedCountry}`)

    if (localDocumentsBriefData) {
      localDocumentsBriefDataToSet = JSON.parse(localDocumentsBriefData)

      if (!Number.isNaN(Number(localDocumentsBriefDataToSet)) && Number(localDocumentsBriefDataToSet) > 0) {
        setProcessingBatch(true)
      } else {
        setProcessingBatch(false)
      }
    }

    return handleGetBatchesBriefDataFromRemote(systemUserId, accessToken)
  }

  const handleGetBatchesBriefDataFromRemote = async (systemUserId: string, accessToken: string) => {
    try {
      const { data } = await getProcessingBatchesData(String(systemUserId), selectedCountry, accessToken)

      if (!Number.isNaN(Number(data.processingBatchesCount))) {
        globalThis.sessionStorage.setItem(`processingBatchesCount:${systemUserId}:${selectedCountry}`, JSON.stringify(data.processingBatchesCount))
        if (!Number.isNaN(Number(data.processingBatchesCount)) && Number(data.processingBatchesCount) > 0) {
          setProcessingBatch(true)
        } else {
          setProcessingBatch(false)
        }
      }
    } catch (error: any) {
      console.log('Error > handleGetBatchesBriefDataFromRemote >', error, error?.response?.data?.message)
    }
  }

  const handleGetCurrentPocketsData = useCallback(async (customerCompanyId: string, userSessionToUse: UserSessionType, accessToken: string) => {
    let localCurrentPocketsDataToSet: any = {}
    let localCustomerCompanyDataToSet: any = {}
    const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
    const localCurrentPocketsData = globalThis.sessionStorage.getItem(`currentPocketsData:${selectedCountry}`)
    const localCustomerCompanyData = globalThis.sessionStorage.getItem(`customerCompanyData:${customerCompanyId}`)

    if (localCurrentPocketsData) {
      localCurrentPocketsDataToSet = JSON.parse(localCurrentPocketsData)
      handleSetPocketsDataInitialData(localCurrentPocketsDataToSet, userSessionToUse)
    }

    if (localCustomerCompanyData) {
      localCustomerCompanyDataToSet = JSON.parse(localCustomerCompanyData)
      setCustomerCompanyData(localCustomerCompanyDataToSet)
    }

    await handleGetPocketsDataFromRemote(customerCompanyId, userSessionToUse, selectedCountry, accessToken)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGetPocketsDataFromRemote = useCallback(async (customerCompanyId: string, userSessionToUse: UserSessionType, selectedCountry: string, accessToken: string) => {
    try {
      const { data } = await getCurrentPocketsDataByCountry(customerCompanyId, selectedCountry, accessToken)

      globalThis.sessionStorage.setItem(`currentPocketsData:${selectedCountry}`, JSON.stringify(data))
      handleSetPocketsDataInitialData(data, userSessionToUse)

      if (data.customerCompanyData) {
        globalThis.sessionStorage.setItem(`customerCompanyData:${customerCompanyId}`, JSON.stringify(data.customerCompanyData))
        setCustomerCompanyData(data.customerCompanyData)
      }
    } catch (error) {
      console.log('Error handleGetPocketsDataFromRemote > ', error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSetPocketsDataInitialData = async (dataToSet: any, userSessionToUse: UserSessionType) => {
    if (dataToSet.pocketsByCountry) {
      if (userSessionToUse?.role === 'super-admin-client') {
        const mainPocketData = dataToSet.pocketsByCountry.find((pocketData: { [key: string]: any }) => pocketData.main === true)

        if (mainPocketData) {
          setDefaultPocketIdSelected(String(mainPocketData._id))
        }
      }

      if ((userSessionToUse?.role === 'admin-client' || userSessionToUse?.role === 'admin-client-builder') && userSessionToUse.adminOfPockets) {
        for (const pocketId of userSessionToUse.adminOfPockets) {
          const defaultPocketData = dataToSet.pocketsByCountry.find((pocketData: { [key: string]: any }) => String(pocketData._id) === pocketId)

          if (defaultPocketData) {
            setDefaultPocketIdSelected(String(defaultPocketData._id))
          }
        }
      }

      setCurrentPocketsData(dataToSet.pocketsByCountry)
    }
  }

  const handleShowCreatingReportNotification = useCallback(() => {
    const notificationKey = enqueueSnackbar(t('notifications.creatingReport'), { persist: true, variant: 'creatingReport', anchorOrigin: { vertical: 'bottom', horizontal: 'right' } })

    return notificationKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleDeleteBatchFromLocalDB = async (documentKey: string) => {
    const documentDataSetted: Array<Document> = (await handleGetDataFromTableById('documents', String(documentKey))) as Array<Document>

    if (documentDataSetted.length > 0) {
      await handleDeleteDataByOne('documents', documentKey)
    }
  }

  const handleShowCreatingReportDelayNotification = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
    }

    enqueueSnackbar(t('notifications.creatingReportDelay'), { variant: 'creatingReportDelay', autoHideDuration: 12000, anchorOrigin: { vertical: 'bottom', horizontal: 'right' } })
    return true
  }

  const value = useMemo<AppStateContextType>(
    () => ({
      homeStats,
      banksData,
      isLoading,
      scrollToTop,
      USDDollarRate,
      setScrollToTop,
      processingBatch,
      handleStopTimer,
      handleStartTimer,
      customersBriefData,
      currentPocketsData,
      customerCompanyData,
      setCustomersBriefData,
      setCurrentPocketsData,
      showProcessingSnackbar,
      defaultPocketIdSelected,
      banksBalancesDataLoaded,
      setShowProcessingSnackbar,
      handleGetCurrentPocketsData,
      handleGetPocketsDataFromRemote,
      loadDashboardAppData,
      bankingPartnersBalanceByCountry,
      handleShowCreatingReportNotification,
      updateLoadingStatus: (loadingStatus: boolean) => {
        if (globalThis.window !== undefined) {
          setIsLoading(loadingStatus)
          globalThis.sessionStorage.setItem('isLoading', String(loadingStatus))
          return true
        }

        return false
      },
      updateBanksBalancesDataLoadedStatus: (banksBalancesDataLoadedStatus: boolean) => {
        if (globalThis.window !== undefined) {
          setBanksBalancesDataLoaded(banksBalancesDataLoadedStatus)
          globalThis.sessionStorage.setItem('banksBalancesDataLoaded', String(banksBalancesDataLoadedStatus))
          return true
        }

        return false
      },
      updateUSDDollarRate: (USDDollarRateToSet: number | string) => {
        if (USDDollarRateToSet === '' && globalThis.window !== undefined) {
          setUSDDollarRate(0)
          globalThis.localStorage.removeItem('USDDollarRate')
          return
        }

        if (globalThis.window !== undefined) {
          setUSDDollarRate(Number(USDDollarRateToSet))
          globalThis.localStorage.setItem('USDDollarRate', String(USDDollarRateToSet))
          return true
        }

        return false
      },
      setCurrentBanksData: (banksDataToSet: Array<Bank>) => {
        if (banksDataToSet.length === 0) {
          setBanksData([])
          globalThis.sessionStorage.removeItem('banksData')
          return
        }

        banksDataToSet.sort((a, b) => a.name.localeCompare(b.name))
        setBanksData(banksDataToSet)
        if (globalThis.window !== undefined) globalThis.sessionStorage.setItem('banksData', JSON.stringify(banksDataToSet))
      }
    }),
    [
      homeStats,
      banksData,
      isLoading,
      scrollToTop,
      USDDollarRate,
      setScrollToTop,
      processingBatch,
      handleStopTimer,
      handleStartTimer,
      customersBriefData,
      currentPocketsData,
      customerCompanyData,
      setCustomersBriefData,
      setCurrentPocketsData,
      showProcessingSnackbar,
      banksBalancesDataLoaded,
      defaultPocketIdSelected,
      setShowProcessingSnackbar,
      handleGetCurrentPocketsData,
      handleGetPocketsDataFromRemote,
      loadDashboardAppData,
      bankingPartnersBalanceByCountry,
      handleShowCreatingReportNotification
    ]
  )

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>
}

const AppStateContextProvider = {
  Provider,
  Consumer: AppStateContext.Consumer
}

export default AppStateContextProvider
